import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/i18n/LanguageContext";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function Register() {
  const { t } = useI18n();
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    phone: "",
    role: "customer",
  });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await register({ ...form, phone: form.phone || null });
      navigate(user.role === "vendor" ? "/driver" : "/bookings");
    } catch (err) {
      toast.error(apiError(err));
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="mx-auto max-w-md px-5 py-16 lg:py-24">
        <h1 className="font-display text-3xl tracking-tight sm:text-4xl">{t("auth.regTitle")}</h1>
        <p className="mt-3 text-base text-slate-600">{t("auth.regSub")}</p>
        <form
          onSubmit={submit}
          data-testid="register-form"
          className="mt-9 space-y-5 rounded-xl border border-slate-200 bg-white p-7 ambient"
        >
          <div>
            <Label htmlFor="name">{t("auth.name")}</Label>
            <Input
              id="name"
              required
              minLength={2}
              data-testid="register-name-input"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="mt-1.5"
            />
          </div>
          <div>
            <Label htmlFor="email">{t("auth.email")}</Label>
            <Input
              id="email"
              type="email"
              required
              data-testid="register-email-input"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="mt-1.5"
            />
          </div>
          <div>
            <Label htmlFor="password">{t("auth.password")}</Label>
            <Input
              id="password"
              type="password"
              required
              minLength={6}
              data-testid="register-password-input"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              className="mt-1.5"
            />
          </div>
          <div>
            <Label htmlFor="phone">{t("auth.phone")}</Label>
            <Input
              id="phone"
              data-testid="register-phone-input"
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              className="mt-1.5"
            />
          </div>
          <div>
            <Label>{t("auth.role")}</Label>
            <div className="mt-1.5 flex gap-2 rounded-md bg-slate-100 p-1 text-sm">
              {[
                { v: "customer", l: t("auth.customer") },
                { v: "vendor", l: t("auth.vendor") },
              ].map((r) => (
                <button
                  key={r.v}
                  type="button"
                  data-testid={`register-role-${r.v}`}
                  onClick={() => setForm({ ...form, role: r.v })}
                  className={`flex-1 rounded px-3 py-2 font-medium transition-colors duration-200 ${
                    form.role === r.v ? "bg-white text-sea shadow-sm" : "text-slate-500"
                  }`}
                >
                  {r.l}
                </button>
              ))}
            </div>
          </div>
          <Button
            type="submit"
            data-testid="register-submit-btn"
            disabled={loading}
            className="h-11 w-full bg-sea hover:bg-sea-dark"
          >
            {loading ? t("common.loading") : t("auth.signup")}
          </Button>
          <p className="text-center text-sm text-slate-600">
            {t("auth.haveAccount")}{" "}
            <Link to="/login" data-testid="go-login-link" className="font-semibold text-sea underline">
              {t("auth.signin")}
            </Link>
          </p>
        </form>
      </div>
      <Footer />
    </div>
  );
}
